import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Download, ArrowLeft, RefreshCw, Check, Clock, X as XIcon, ArrowRightLeft, ArrowUpFromLine, Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { userApi, useUserAuth, formatApiErrorDetail } from "../lib/UserAuthContext";
import { LOGO_URL } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n, d = 2) => Number.isFinite(+n)
  ? (+n).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d })
  : "—";

const statusBadge = {
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
};
const statusIcon = { completed: Check, paid: Check, pending: Clock, cancelled: XIcon, rejected: XIcon };
const kindIcon = { transaction: Send, conversion: ArrowRightLeft, withdrawal: ArrowUpFromLine };
const kindLabel = { transaction: "Transfer", conversion: "Conversion", withdrawal: "Withdrawal" };

export default function TransactionHistory() {
  const { user } = useUserAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | transaction | conversion | withdrawal
  const [downloading, setDownloading] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [txs, cvs, wds] = await Promise.all([
        userApi.get("/transactions"),
        userApi.get("/conversions"),
        userApi.get("/withdrawals"),
      ]);
      const merged = [
        ...txs.data.map((t) => ({
          kind: "transaction", id: t.id, when: t.created_at,
          pair: `${t.from_currency}/${t.to_currency}`,
          from_currency: t.from_currency, to_currency: t.to_currency,
          from_amount: t.from_amount, to_amount: t.to_amount,
          rate: t.rate, status: t.status,
        })),
        ...cvs.data.map((c) => ({
          kind: "conversion", id: c.id, when: c.created_at,
          pair: `${c.from_currency}/${c.to_currency}`,
          from_currency: c.from_currency, to_currency: c.to_currency,
          from_amount: c.from_amount, to_amount: c.to_amount,
          rate: c.rate, status: "completed",
        })),
        ...wds.data.map((w) => ({
          kind: "withdrawal", id: w.id, when: w.created_at,
          pair: `${w.currency} · ${w.method.replace(/_/g, " ")}`,
          from_currency: w.currency, to_currency: "—",
          from_amount: w.amount, to_amount: null,
          rate: null, status: w.status,
        })),
      ].sort((a, b) => (a.when < b.when ? 1 : -1));
      setItems(merged);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => (filter === "all" ? items : items.filter((i) => i.kind === filter)),
    [items, filter]
  );

  const counts = useMemo(() => items.reduce((a, i) => {
    a[i.kind] = (a[i.kind] || 0) + 1;
    return a;
  }, {}), [items]);

  const downloadReceipt = async (row) => {
    setDownloading(`${row.kind}-${row.id}`);
    try {
      const res = await userApi.get(`/receipts/${row.kind}/${row.id}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cashpark-${row.kind}-${row.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 800);
      toast.success("Receipt downloaded");
    } catch (e) {
      toast.error("Could not download receipt");
    } finally { setDownloading(null); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="history-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="CashPark" className="h-8 w-8" />
            <span className="font-display font-bold text-[#0B192C]">CashPark <span className="text-[#FF6B00]">Inc</span></span>
          </Link>
          <Button variant="outline" onClick={() => nav("/dashboard")} className="rounded-full h-9 px-4 text-sm" data-testid="back-to-dashboard">
            <ArrowLeft size={14} className="mr-1.5" /> Dashboard
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold text-[#0B192C]">Transaction history</h1>
            <p className="text-sm text-slate-500 mt-1">
              {user ? <>All activity for <span className="font-semibold text-[#0B192C]">{user.email}</span></> : "Sign in to view your history"}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            {[
              { k: "all", label: "All" },
              { k: "transaction", label: "Transfers" },
              { k: "conversion", label: "Conversions" },
              { k: "withdrawal", label: "Withdrawals" },
            ].map((f) => (
              <button
                key={f.k}
                onClick={() => setFilter(f.k)}
                data-testid={`history-filter-${f.k}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${filter === f.k ? "bg-[#0B192C] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-[#0B192C]"}`}
              >
                {f.label}{f.k !== "all" ? ` (${counts[f.k] || 0})` : ` (${items.length})`}
              </button>
            ))}
            <Button variant="outline" onClick={load} className="rounded-full h-9 px-4 text-sm" data-testid="history-refresh">
              <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        </div>

        <div className="mt-6 bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-slate-500 text-sm" data-testid="history-loading">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-slate-500 text-sm text-center" data-testid="history-empty">
              No {filter === "all" ? "" : filter} activity yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="history-table">
                <thead className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                  <tr>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Pair</th>
                    <th className="px-4 py-3">Rate</th>
                    <th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Receipt</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((row) => {
                    const KindIcon = kindIcon[row.kind] || Clock;
                    const StatusIcon = statusIcon[row.status] || Clock;
                    return (
                      <tr key={`${row.kind}-${row.id}`} data-testid={`history-row-${row.id}`} className="hover:bg-slate-50/50">
                        <td className="px-4 py-3">
                          <div className="text-[#0B192C] font-medium" data-testid={`history-date-${row.id}`}>
                            {new Date(row.when).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" })}
                          </div>
                          <div className="text-xs text-slate-500">
                            {new Date(row.when).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-orange-50 text-[#E65C00] text-xs font-semibold">
                            <KindIcon size={12} /> {kindLabel[row.kind]}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-display font-semibold text-[#0B192C]" data-testid={`history-pair-${row.id}`}>{row.pair}</td>
                        <td className="px-4 py-3 text-slate-600 text-xs font-mono" data-testid={`history-rate-${row.id}`}>
                          {row.rate ? row.rate.toLocaleString("en-US", { maximumFractionDigits: 8 }) : "—"}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-[#0B192C] font-medium">
                            {row.from_currency === "CAD" ? "$" : row.from_currency === "NGN" ? "₦" : ""}
                            {fmt(row.from_amount)} <span className="text-slate-500 font-normal text-xs">{row.from_currency}</span>
                          </div>
                          {row.to_amount != null && (
                            <div className="text-xs text-slate-500">
                              → {row.to_currency === "NGN" ? "₦" : "$"}{fmt(row.to_amount)} {row.to_currency}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            data-testid={`history-status-${row.id}`}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs font-semibold ${statusBadge[row.status] || statusBadge.pending}`}
                          >
                            <StatusIcon size={12} /> {row.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadReceipt(row)}
                            disabled={downloading === `${row.kind}-${row.id}`}
                            data-testid={`history-receipt-${row.id}`}
                            className="rounded-full h-8 text-xs"
                          >
                            <Download size={12} className="mr-1" />
                            {downloading === `${row.kind}-${row.id}` ? "…" : "Receipt"}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
